import { Paper, MenuList, Box, TextField, Button, Divider } from "@mui/material";
import { CustomEventType } from "../../utils/types";
import { TechnicianSelector } from "./TechnicianSelector";
import { CopyToAnotherWeek } from "./CopyToAnotherWeek";
import { CopyToAnotherDay } from "./CopyToAnotherDay";
import { CopyToRoom } from "./CopyToRoom";
import { MenuItems } from "./MenuItems";
import { TimeChanger } from "./TimeChanger";
import { NotesUpdater } from "./NotesUpdater";
import { useScheduleActions } from "./useScheduleActions";
import { ServicesUpdater } from "./ServicesUpdater";
import { RoomUpdater } from "./RoomUpdater";
import { ClinicUpdater } from "./ClinicUpdater";
import { RepeatUntilDate } from "./RepeatUntilDate";


type SelectedEventsActionsProps = {
    selectedEvents: Array<CustomEventType>;
    users: any;
    setSelectedEvents: (events: Array<CustomEventType>) => void;
    selectMode: boolean;
    setSelectMode: (value: boolean) => void;
    filteredEvents: Array<CustomEventType>;
    resources: any;
    clinics: any;
    currentDate: Date;
};

export const SelectedEventsActions = ({
    selectedEvents,
    users,
    setSelectedEvents,
    selectMode,
    setSelectMode,
    filteredEvents,
    resources,
    clinics,
    currentDate,
}: SelectedEventsActionsProps) => {
    const {
        formMethods,
        watchedValues,
        isWeekModeActive,
        isCopying,
        isUpdatingTimes,
        isUpdatingNotes,
        isUpdatingRoom,
        isUpdatingClinic,
        isDeleting,
        isConverting,
        copyEventsToTargetDatesOrWeek,
        handleUpdateTechnician,
        handleUpdateTimes,
        handleUpdateNotes,
        handleUpdateServices,
        handleRemoveServices,
        handleUpdateRoom,
        handleUpdateClinic,
        handleConvertToTimeOff,
        resetFormAndCloseDialog,
        handleSelectAll,
        handleDelete,
        actionMode,
        isUpdateMode,
        handleActionModeChange,
    } = useScheduleActions({
        selectedEvents,
        setSelectedEvents,
        setSelectMode,
        filteredEvents,
    });

    const { control } = formMethods;

    if (!selectMode) {
        return null;
    }

    const handleWeekChange = (weeks: Array<string>) => {
        // Clear other selections when week mode is activated
        if (weeks.length > 0) {
            formMethods.setValue("selectedDays", []);
            formMethods.setValue("numberOfCopies", 1);
            formMethods.setValue("selectedResource", null);
        }
    };

    return (
        <Paper
            elevation={2}
            sx={{
                position: "fixed",
                bottom: 20,
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 1000,
                width: 300,
                p: 2,
            }}
        >
            <MenuList sx={{ p: 0 }}>
                <Box mb={1}>
                    <CopyToAnotherWeek control={control} currentDate={currentDate} onWeekChange={handleWeekChange} />
                    <CopyToAnotherDay control={control} isWeekModeActive={isWeekModeActive} />
                    {!isWeekModeActive && (
                        <TextField
                            label="Número de cópias"
                            type="number"
                            size="small"
                            fullWidth
                            value={watchedValues.numberOfCopies || ""}
                            placeholder="1"
                            onChange={(e) => {
                                const value = e.target.value === "" ? 0 : parseInt(e.target.value);
                                formMethods.setValue("numberOfCopies", value);
                            }}
                            inputProps={{ min: 1 }}
                            sx={{ mt: 4 }}
                            disabled={isWeekModeActive}
                        />
                    )}
                </Box>
                <Box mb={1}>
                    <CopyToRoom control={control} isWeekModeActive={isWeekModeActive} resources={resources} />
                </Box>

                <RepeatUntilDate
                    formMethods={formMethods}
                    watchedValues={watchedValues}
                    selectedEvents={selectedEvents}
                />

                {actionMode === null ? (
                    <MenuItems
                        selectedEvents={selectedEvents}
                        setSelectedEvents={setSelectedEvents}
                        onActionModeChange={handleActionModeChange}
                        onCopyEvents={copyEventsToTargetDatesOrWeek}
                        // from useScheduleActions
                        formMethods={formMethods}
                        watchedValues={watchedValues}
                        isWeekModeActive={isWeekModeActive}
                        isCopying={isCopying}
                        isDeleting={isDeleting}
                        isConverting={isConverting}
                        handleSelectAll={handleSelectAll}
                        handleDelete={handleDelete}
                        handleConvertToTimeOff={handleConvertToTimeOff}
                    />
                ) : actionMode === "copy" || actionMode === "update" ? (
                    <Box p={2}>
                        <TechnicianSelector
                            users={users}
                            onSelect={(userIds) =>
                                isUpdateMode ? handleUpdateTechnician(userIds) : copyEventsToTargetDatesOrWeek(userIds)
                            }
                            mode={isUpdateMode ? "update" : "copy"}
                            isCopying={isCopying}
                        />
                        <Button
                            fullWidth
                            color="secondary"
                            size="small"
                            onClick={() => {
                                handleActionModeChange(null);
                            }}
                            sx={{ mt: 1 }}
                        >
                            Cancelar
                        </Button>
                    </Box>
                ) : actionMode === "time" ? (
                    <TimeChanger
                        control={control}
                        isUpdatingTimes={isUpdatingTimes}
                        onUpdateTimes={handleUpdateTimes}
                        onCancel={() => handleActionModeChange(null)}
                    />
                ) : actionMode === "services" ? (
                    <ServicesUpdater
                        control={control}
                        isUpdatingServices={false}
                        onUpdateServices={handleUpdateServices}
                        onRemoveServices={handleRemoveServices}
                        onCancel={() => handleActionModeChange(null)}
                    />
                ) : actionMode === "room" ? (
                    <RoomUpdater
                        control={control}
                        resources={resources}
                        isUpdatingRoom={isUpdatingRoom}
                        onUpdateRoom={handleUpdateRoom}
                        onCancel={() => handleActionModeChange(null)}
                    />
                ) : actionMode === "clinic" ? (
                    <ClinicUpdater
                        control={control}
                        clinics={clinics}
                        isUpdatingClinic={isUpdatingClinic}
                        onUpdateClinic={handleUpdateClinic}
                        onCancel={() => handleActionModeChange(null)}
                    />
                ) : (
                    <NotesUpdater
                        control={control}
                        isUpdatingNotes={isUpdatingNotes}
                        onUpdateNotes={handleUpdateNotes}
                        onCancel={() => handleActionModeChange(null)}
                    />
                )}
            </MenuList>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Button
                    fullWidth
                    variant="contained"
                    size="small"
                    color="secondary"
                    onClick={() => {
                        resetFormAndCloseDialog();
                    }}
                >
                    Cancelar
                </Button>
            </Box>
        </Paper>
    );
};
